import React, { useEffect, useRef } from 'react';
import { Redirect, useHistory, useParams } from 'react-router-dom';

import i18n from 'i18n-js';

import {
    formatGuestName,
    formatReservationDate,
    formValuesToModel,
    getDatePart,
    ReservationFormValues,
    reservationToFormValues,
    validateReservationForm,
} from 'helpers/reservationHelper';
import { useAppSelector } from 'index';
import globalStore from 'store/globalStore';
import { ToasterType } from 'store/globalStore/actionCreators';
import reservationsStore from 'store/reservationsStore';
import ReservationEditor from './components/ReservationEditor';
import { RESERVATIONS_PATH, useReservationsSubpageGuard, useThunkDispatch } from './hooks';
import { ACTIVE_TAB_STATE } from './index';

export default function ProcessRequestPage() {
    const { id } = useParams<{ id: string }>();
    const history = useHistory();
    const dispatch = useThunkDispatch();
    const isModuleDisabled = useReservationsSubpageGuard();
    const requests = useAppSelector((state) => state.reservations.requests);
    const isRequestsLoaded = useAppSelector(
        (state) => state.reservations.requestsRestaurantId === state.user.currentRestaurantId
    );
    const storedRequest = requests.find((item) => item.id === id);
    const requestRef = useRef(storedRequest);
    if (storedRequest) {
        requestRef.current = storedRequest;
    }
    const request = requestRef.current;

    useEffect(() => {
        dispatch(reservationsStore.actionCreators.selectRequest(id));
    }, [id]); // eslint-disable-line react-hooks/exhaustive-deps

    const showToaster = (type: ToasterType, message: unknown) =>
        dispatch(globalStore.actionCreators.showToaster(type, message));

    const backToRequests = () => history.replace(RESERVATIONS_PATH);

    const handleSave = async (values: ReservationFormValues) => {
        try {
            const result = await dispatch(
                reservationsStore.actionCreators.saveRequest(id, formValuesToModel(values))
            );
            if (result.status === 'saved') {
                showToaster('success', i18n.t('messages.updatedSuccesfully'));
            } else {
                showToaster('error', i18n.t('reservations.alreadyHandled'));
            }
            backToRequests();
        } catch (error) {
            showToaster('error', error);
        }
    };

    const handleConfirm = async (values: ReservationFormValues): Promise<string | null> => {
        try {
            const result = await dispatch(
                reservationsStore.actionCreators.confirmRequest(id, formValuesToModel(values))
            );
            if (result.status === 'tableTaken') {
                return result.tableId;
            }
            if (result.status === 'alreadyHandled') {
                showToaster('error', i18n.t('reservations.alreadyHandled'));
                backToRequests();
                return null;
            }
            const { email, reservationStartDateTime } = result.reservation;
            showToaster(
                'success',
                email
                    ? i18n.t('reservations.confirmedEmailSent', { email })
                    : i18n.t('reservations.confirmedNoEmail')
            );
            history.replace(RESERVATIONS_PATH, {
                ...ACTIVE_TAB_STATE,
                date: getDatePart(reservationStartDateTime),
            });
        } catch (error) {
            showToaster('error', error);
        }
        return null;
    };

    if (isModuleDisabled) {
        return <Redirect to={RESERVATIONS_PATH} />;
    }
    if (!request) {
        return isRequestsLoaded ? <Redirect to={RESERVATIONS_PATH} /> : null;
    }

    return (
        <ReservationEditor
            title={i18n
                .t('reservations.processRequestTitle', { name: formatGuestName(request) })
                .toUpperCase()}
            channelLine={i18n.t('reservations.webAppRequestReceived', {
                dateTime: formatReservationDate(request.createDate, 'dd.MM.yyyy, HH:mm'),
            })}
            reservationId={request.id}
            initialValues={reservationToFormValues(request)}
            validate={validateReservationForm}
            confirmLabel={i18n.t('reservations.confirmReservation')}
            onCancel={backToRequests}
            onSave={handleSave}
            onConfirm={handleConfirm}
        />
    );
}
